/**
 * Realtime API Tester Page
 * Interactive tool for testing OpenAI-compatible Realtime API services
 */

import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Square, Trash2 } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { localizePath } from '@/lib/locale-path';
import { useDocumentMeta } from '@/lib/document-meta';
import './tutorials.scss';

interface Translation {
  pageTitle: string;
  backLink: string;
  overview: string;
  securityWarning: string;
  testInterface: {
    title: string;
    description: string;
    presetServices: string;
    presetDescription: string;
    apiEndpoint: string;
    apiEndpointPlaceholder: string;
    apiEndpointHelper: string;
    apiKey: string;
    apiKeyPlaceholder: string;
    apiKeyHelper: string;
    model: string;
    modelHelper: string;
    runTests: string;
    stopTests: string;
    clearResults: string;
  };
  testSteps: {
    title: string;
    apiKeyValidation: { title: string; testing: string; success: string; failed: string };
    modelAvailability: { title: string; testing: string; success: string; failed: string };
    websocketConnection: { title: string; testing: string; success: string; failed: string };
    sessionCreation: { title: string; testing: string; success: string; failed: string };
    messageTest: { title: string; testing: string; success: string; failed: string };
  };
  connectionLog: string;
  testSummary: {
    allPassed: string;
    someFailed: string;
    checkList: string;
  };
  troubleshooting: string[];
  howItWorks: {
    title: string;
    description: string;
    steps: string[];
  };
  supportedServices: {
    title: string;
    description: string;
    list: string[];
  };
  securityNote: {
    title: string;
    content: string;
  };
}

const translations: Record<Locale, Translation> = {
  en: {
    pageTitle: 'Realtime API Tester',
    backLink: 'Back to AI Providers',
    overview: 'This tool allows you to test the availability and functionality of OpenAI\'s Realtime API and OpenAI-compatible services. You can verify API key permissions, test WebSocket connectivity, and send test messages to ensure everything is working correctly.',
    securityWarning: 'Security Warning: This tester runs in your browser and exposes your API key in the WebSocket connection. This method is ONLY for testing purposes and should NEVER be used in production. For production use, implement a secure relay server to handle authentication.',
    testInterface: {
      title: 'Test Interface',
      description: 'Configure your API endpoint and key to test the realtime service availability.',
      presetServices: 'Preset Services',
      presetDescription: 'Click to quickly fill in common service endpoints:',
      apiEndpoint: 'API Endpoint',
      apiEndpointPlaceholder: 'e.g., https://api.openai.com',
      apiEndpointHelper: 'The base URL of the API service (without /v1 or paths)',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Your API key for authentication',
      model: 'Model',
      modelHelper: 'Select the realtime model to test',
      runTests: 'Run Tests',
      stopTests: 'Stop Tests',
      clearResults: 'Clear Results',
    },
    testSteps: {
      title: 'Test Steps',
      apiKeyValidation: {
        title: 'API Key Validation',
        testing: 'Validating API key format and permissions...',
        success: 'API key is valid and has required permissions',
        failed: 'API key validation failed',
      },
      modelAvailability: {
        title: 'Model Availability',
        testing: 'Checking if realtime models are available...',
        success: 'Realtime models found: ',
        failed: 'No realtime models available',
      },
      websocketConnection: {
        title: 'WebSocket Connection',
        testing: 'Establishing WebSocket connection to realtime API...',
        success: 'WebSocket connection established successfully',
        failed: 'WebSocket connection failed',
      },
      sessionCreation: {
        title: 'Session Creation',
        testing: 'Creating realtime session...',
        success: 'Session created successfully',
        failed: 'Session creation failed',
      },
      messageTest: {
        title: 'Message Test',
        testing: 'Sending test message and waiting for response...',
        success: 'Received response: ',
        failed: 'No response received or message test failed',
      },
    },
    connectionLog: 'Connection Log',
    testSummary: {
      allPassed: 'All tests passed! The realtime API is fully functional.',
      someFailed: 'Some tests failed. Please review the errors above and check your configuration.',
      checkList: 'Checklist for troubleshooting:',
    },
    troubleshooting: [
      'Verify your API key is correct and has not expired',
      'Ensure your account has access to realtime models',
      'Check your internet connection and firewall settings',
      'Verify the API endpoint URL is correct',
      'If testing from browser, check for CORS restrictions',
      'Ensure your account has sufficient credits and active billing',
      'Contact your service provider to confirm realtime API is working properly',
    ],
    howItWorks: {
      title: 'How This Tester Works',
      description: 'This tester checks a realtime API service in these steps:',
      steps: [
        'Validates the API key format and checks basic API access',
        'Queries available models to confirm realtime model access',
        'Establishes a WebSocket connection to the realtime endpoint',
        'Creates a session with the realtime API',
        'Sends a test message and validates the response',
      ],
    },
    supportedServices: {
      title: 'Supported Services',
      description: 'This tester works with the following services:',
      list: [
        'OpenAI Realtime API (official)',
        'CometAPI (OpenAI-compatible)',
        'Kizuna AI API (OpenAI-compatible)',
        'Any OpenAI-compatible realtime service',
      ],
    },
    securityNote: {
      title: 'Security Note',
      content: 'This tester uses the \'openai-insecure-api-key\' WebSocket protocol for browser-based testing, which exposes your API key. This is ONLY suitable for testing and development. For production applications, always use a secure relay server to protect your API credentials.',
    },
  },
  zh: {
    pageTitle: '实时 API 测试器',
    backLink: '返回 AI 提供商',
    overview: '此工具允许您测试 OpenAI 实时 API 和 OpenAI 兼容服务的可用性和功能。您可以验证 API 密钥权限、测试 WebSocket 连接并发送测试消息以确保一切正常工作。',
    securityWarning: '安全警告：此测试器在浏览器中运行，会在 WebSocket 连接中暴露您的 API 密钥。此方法仅用于测试目的，绝不应在生产环境中使用。生产环境请实现安全的中继服务器来处理身份验证。',
    testInterface: {
      title: '测试界面',
      description: '配置您的 API 端点和密钥以测试实时服务可用性。',
      presetServices: '预设服务',
      presetDescription: '点击快速填入常见服务端点：',
      apiEndpoint: 'API 端点',
      apiEndpointPlaceholder: '例如：https://api.openai.com',
      apiEndpointHelper: 'API 服务的基础 URL（不含 /v1 或路径）',
      apiKey: 'API 密钥',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: '用于身份验证的 API 密钥',
      model: '模型',
      modelHelper: '选择要测试的实时模型',
      runTests: '运行测试',
      stopTests: '停止测试',
      clearResults: '清除结果',
    },
    testSteps: {
      title: '测试步骤',
      apiKeyValidation: {
        title: 'API 密钥验证',
        testing: '验证 API 密钥格式和权限...',
        success: 'API 密钥有效且具有所需权限',
        failed: 'API 密钥验证失败',
      },
      modelAvailability: {
        title: '模型可用性',
        testing: '检查实时模型是否可用...',
        success: '找到实时模型：',
        failed: '没有可用的实时模型',
      },
      websocketConnection: {
        title: 'WebSocket 连接',
        testing: '建立到实时 API 的 WebSocket 连接...',
        success: 'WebSocket 连接成功建立',
        failed: 'WebSocket 连接失败',
      },
      sessionCreation: {
        title: '会话创建',
        testing: '创建实时会话...',
        success: '会话创建成功',
        failed: '会话创建失败',
      },
      messageTest: {
        title: '消息测试',
        testing: '发送测试消息并等待响应...',
        success: '收到响应：',
        failed: '未收到响应或消息测试失败',
      },
    },
    connectionLog: '连接日志',
    testSummary: {
      allPassed: '所有测试通过！实时 API 功能完全正常。',
      someFailed: '部分测试失败。请查看上述错误并检查您的配置。',
      checkList: '故障排除清单：',
    },
    troubleshooting: [
      '验证您的 API 密钥是否正确且未过期',
      '确保您的账户可以访问实时模型',
      '检查您的网络连接和防火墙设置',
      '验证 API 端点 URL 是否正确',
      '如果从浏览器测试，检查 CORS 限制',
      '确保您的账户有足够的额度和有效的计费',
      '联系您的服务提供商确认实时 API 是否正常工作',
    ],
    howItWorks: {
      title: '测试器工作原理',
      description: '此测试器通过以下步骤对实时 API 服务进行全面验证：',
      steps: [
        '验证 API 密钥格式并检查基本 API 访问',
        '查询可用模型以确认实时模型访问权限',
        '建立到实时端点的 WebSocket 连接',
        '使用实时 API 创建会话',
        '发送测试消息并验证响应',
      ],
    },
    supportedServices: {
      title: '支持的服务',
      description: '此测试器适用于以下服务：',
      list: [
        'OpenAI 实时 API（官方）',
        'CometAPI（OpenAI 兼容）',
        'Kizuna AI API（OpenAI 兼容）',
        '任何 OpenAI 兼容的实时服务',
      ],
    },
    securityNote: {
      title: '安全提示',
      content: '此测试器使用 \'openai-insecure-api-key\' WebSocket 协议进行基于浏览器的测试，这会暴露您的 API 密钥。这仅适用于测试和开发。在生产应用中，请始终使用安全的中继服务器来保护您的 API 凭据。',
    },
  },
  ja: {
    pageTitle: 'リアルタイム API テスター',
    backLink: 'AI プロバイダーに戻る',
    overview: 'このツールを使用して、OpenAI のリアルタイム API と OpenAI 互換サービスの可用性と機能をテストできます。API キーの権限を確認し、WebSocket 接続をテストし、テストメッセージを送信して、すべてが正常に動作していることを確認できます。',
    securityWarning: 'セキュリティ警告：このテスターはブラウザで実行され、WebSocket 接続で API キーが公開されます。この方法はテスト目的のみで、本番環境では絶対に使用しないでください。本番環境では、認証を処理する安全なリレーサーバーを実装してください。',
    testInterface: {
      title: 'テストインターフェース',
      description: 'API エンドポイントとキーを設定して、リアルタイムサービスの可用性をテストします。',
      presetServices: 'プリセットサービス',
      presetDescription: 'クリックして一般的なサービスエンドポイントをすばやく入力：',
      apiEndpoint: 'API エンドポイント',
      apiEndpointPlaceholder: '例：https://api.openai.com',
      apiEndpointHelper: 'API サービスのベース URL（/v1 やパスを含まない）',
      apiKey: 'API キー',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: '認証用の API キー',
      model: 'モデル',
      modelHelper: 'テストするリアルタイムモデルを選択',
      runTests: 'テストを実行',
      stopTests: 'テストを停止',
      clearResults: '結果をクリア',
    },
    testSteps: {
      title: 'テスト手順',
      apiKeyValidation: {
        title: 'API キー検証',
        testing: 'API キーの形式と権限を検証中...',
        success: 'API キーは有効で、必要な権限があります',
        failed: 'API キーの検証に失敗しました',
      },
      modelAvailability: {
        title: 'モデルの可用性',
        testing: 'リアルタイムモデルが利用可能か確認中...',
        success: 'リアルタイムモデルが見つかりました：',
        failed: '利用可能なリアルタイムモデルがありません',
      },
      websocketConnection: {
        title: 'WebSocket 接続',
        testing: 'リアルタイム API への WebSocket 接続を確立中...',
        success: 'WebSocket 接続が正常に確立されました',
        failed: 'WebSocket 接続に失敗しました',
      },
      sessionCreation: {
        title: 'セッション作成',
        testing: 'リアルタイムセッションを作成中...',
        success: 'セッションが正常に作成されました',
        failed: 'セッション作成に失敗しました',
      },
      messageTest: {
        title: 'メッセージテスト',
        testing: 'テストメッセージを送信し、応答を待機中...',
        success: '応答を受信しました：',
        failed: '応答が受信されなかったか、メッセージテストに失敗しました',
      },
    },
    connectionLog: '接続ログ',
    testSummary: {
      allPassed: 'すべてのテストに合格しました！リアルタイム API は完全に機能しています。',
      someFailed: '一部のテストが失敗しました。上記のエラーを確認し、設定を確認してください。',
      checkList: 'トラブルシューティングのチェックリスト：',
    },
    troubleshooting: [
      'API キーが正しく、期限切れでないことを確認',
      'アカウントがリアルタイムモデルにアクセスできることを確認',
      'インターネット接続とファイアウォール設定を確認',
      'API エンドポイント URL が正しいことを確認',
      'ブラウザからテストする場合、CORS 制限を確認',
      'アカウントに十分なクレジットとアクティブな請求があることを確認',
      'サービスプロバイダーに連絡してリアルタイム API が正常に動作しているか確認',
    ],
    howItWorks: {
      title: 'テスターの仕組み',
      description: 'このテスターは、次の手順でリアルタイム API サービスの包括的な検証を行います：',
      steps: [
        'API キーの形式を検証し、基本的な API アクセスを確認',
        '利用可能なモデルをクエリしてリアルタイムモデルアクセスを確認',
        'リアルタイムエンドポイントへの WebSocket 接続を確立',
        'リアルタイム API でセッションを作成',
        'テストメッセージを送信し、応答を検証',
      ],
    },
    supportedServices: {
      title: '対応サービス',
      description: 'このテスターは次のサービスで動作します：',
      list: [
        'OpenAI リアルタイム API（公式）',
        'CometAPI（OpenAI 互換）',
        'Kizuna AI API（OpenAI 互換）',
        'OpenAI 互換のリアルタイムサービス',
      ],
    },
    securityNote: {
      title: 'セキュリティ通知',
      content: 'このテスターはブラウザベースのテスト用に \'openai-insecure-api-key\' WebSocket プロトコルを使用し、API キーを公開します。これはテストと開発のみに適しています。本番アプリケーションでは、必ず安全なリレーサーバーを使用して API 認証情報を保護してください。',
    },
  },
  ko: {
    pageTitle: '실시간 API 테스터',
    backLink: 'AI 제공업체로 돌아가기',
    overview: '이 도구를 사용하면 OpenAI의 실시간 API 및 OpenAI 호환 서비스의 가용성과 기능을 테스트할 수 있습니다. API 키 권한을 확인하고, WebSocket 연결을 테스트하고, 테스트 메시지를 보내 모든 것이 올바르게 작동하는지 확인할 수 있습니다.',
    securityWarning: '보안 경고: 이 테스터는 브라우저에서 실행되며 WebSocket 연결에서 API 키가 노출됩니다. 이 방법은 테스트 목적으로만 사용해야 하며 프로덕션에서는 절대 사용하지 마세요. 프로덕션 환경에서는 인증을 처리하는 보안 릴레이 서버를 구현하세요.',
    testInterface: {
      title: '테스트 인터페이스',
      description: '실시간 서비스 가용성을 테스트하려면 API 엔드포인트와 키를 구성하세요.',
      presetServices: '사전 설정 서비스',
      presetDescription: '일반적인 서비스 엔드포인트를 빠르게 입력하려면 클릭:',
      apiEndpoint: 'API 엔드포인트',
      apiEndpointPlaceholder: '예: https://api.openai.com',
      apiEndpointHelper: 'API 서비스의 기본 URL (/v1 또는 경로 제외)',
      apiKey: 'API 키',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: '인증용 API 키',
      model: '모델',
      modelHelper: '테스트할 실시간 모델 선택',
      runTests: '테스트 실행',
      stopTests: '테스트 중지',
      clearResults: '결과 지우기',
    },
    testSteps: {
      title: '테스트 단계',
      apiKeyValidation: {
        title: 'API 키 검증',
        testing: 'API 키 형식 및 권한 검증 중...',
        success: 'API 키가 유효하고 필요한 권한이 있습니다',
        failed: 'API 키 검증 실패',
      },
      modelAvailability: {
        title: '모델 가용성',
        testing: '실시간 모델 사용 가능 여부 확인 중...',
        success: '실시간 모델 발견: ',
        failed: '사용 가능한 실시간 모델 없음',
      },
      websocketConnection: {
        title: 'WebSocket 연결',
        testing: '실시간 API에 WebSocket 연결 설정 중...',
        success: 'WebSocket 연결이 성공적으로 설정되었습니다',
        failed: 'WebSocket 연결 실패',
      },
      sessionCreation: {
        title: '세션 생성',
        testing: '실시간 세션 생성 중...',
        success: '세션이 성공적으로 생성되었습니다',
        failed: '세션 생성 실패',
      },
      messageTest: {
        title: '메시지 테스트',
        testing: '테스트 메시지 전송 및 응답 대기 중...',
        success: '응답 수신: ',
        failed: '응답을 받지 못했거나 메시지 테스트 실패',
      },
    },
    connectionLog: '연결 로그',
    testSummary: {
      allPassed: '모든 테스트 통과! 실시간 API가 완전히 작동합니다.',
      someFailed: '일부 테스트 실패. 위의 오류를 검토하고 구성을 확인하세요.',
      checkList: '문제 해결 체크리스트:',
    },
    troubleshooting: [
      'API 키가 올바르고 만료되지 않았는지 확인',
      '계정이 실시간 모델에 액세스할 수 있는지 확인',
      '인터넷 연결 및 방화벽 설정 확인',
      'API 엔드포인트 URL이 올바른지 확인',
      '브라우저에서 테스트하는 경우 CORS 제한 확인',
      '계정에 충분한 크레딧과 활성 결제가 있는지 확인',
      '서비스 제공자에게 연락하여 실시간 API가 정상 작동하는지 확인',
    ],
    howItWorks: {
      title: '테스터 작동 방식',
      description: '이 테스터는 다음 단계를 통해 실시간 API 서비스를 포괄적으로 검증합니다:',
      steps: [
        'API 키 형식 검증 및 기본 API 액세스 확인',
        '실시간 모델 액세스 확인을 위한 사용 가능한 모델 쿼리',
        '실시간 엔드포인트에 WebSocket 연결 설정',
        '실시간 API로 세션 생성',
        '테스트 메시지 전송 및 응답 검증',
      ],
    },
    supportedServices: {
      title: '지원되는 서비스',
      description: '이 테스터는 다음 서비스에서 작동합니다:',
      list: [
        'OpenAI 실시간 API (공식)',
        'CometAPI (OpenAI 호환)',
        'Kizuna AI API (OpenAI 호환)',
        'OpenAI 호환 실시간 서비스',
      ],
    },
    securityNote: {
      title: '보안 공지',
      content: '이 테스터는 브라우저 기반 테스트를 위해 \'openai-insecure-api-key\' WebSocket 프로토콜을 사용하여 API 키를 노출합니다. 이는 테스트 및 개발에만 적합합니다. 프로덕션 애플리케이션에서는 항상 보안 릴레이 서버를 사용하여 API 자격 증명을 보호하세요.',
    },
  },
  es: {
    pageTitle: 'Probador de API en tiempo real',
    backLink: 'Volver a proveedores de IA',
    overview: 'Esta herramienta le permite probar la disponibilidad y funcionalidad de la API en tiempo real de OpenAI y servicios compatibles con OpenAI. Puede verificar los permisos de la clave API, probar la conectividad WebSocket y enviar mensajes de prueba para asegurarse de que todo funcione correctamente.',
    securityWarning: 'Advertencia de seguridad: Este probador se ejecuta en su navegador y expone su clave API en la conexión WebSocket. Este método es SOLO para fines de prueba y NUNCA debe usarse en producción. Para uso en producción, implemente un servidor de retransmisión seguro para manejar la autenticación.',
    testInterface: {
      title: 'Interfaz de prueba',
      description: 'Configure su punto de acceso API y clave para probar la disponibilidad del servicio en tiempo real.',
      presetServices: 'Servicios preconfigurados',
      presetDescription: 'Haga clic para completar rápidamente los puntos de acceso comunes:',
      apiEndpoint: 'Punto de acceso API',
      apiEndpointPlaceholder: 'p. ej., https://api.openai.com',
      apiEndpointHelper: 'La URL base del servicio API (sin /v1 ni rutas)',
      apiKey: 'Clave API',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Su clave API para autenticación',
      model: 'Modelo',
      modelHelper: 'Seleccione el modelo en tiempo real a probar',
      runTests: 'Ejecutar pruebas',
      stopTests: 'Detener pruebas',
      clearResults: 'Limpiar resultados',
    },
    testSteps: {
      title: 'Pasos de prueba',
      apiKeyValidation: {
        title: 'Validación de clave API',
        testing: 'Validando formato y permisos de la clave API...',
        success: 'La clave API es válida y tiene los permisos requeridos',
        failed: 'La validación de la clave API falló',
      },
      modelAvailability: {
        title: 'Disponibilidad del modelo',
        testing: 'Verificando si los modelos en tiempo real están disponibles...',
        success: 'Modelos en tiempo real encontrados: ',
        failed: 'No hay modelos en tiempo real disponibles',
      },
      websocketConnection: {
        title: 'Conexión WebSocket',
        testing: 'Estableciendo conexión WebSocket a la API en tiempo real...',
        success: 'Conexión WebSocket establecida correctamente',
        failed: 'La conexión WebSocket falló',
      },
      sessionCreation: {
        title: 'Creación de sesión',
        testing: 'Creando sesión en tiempo real...',
        success: 'Sesión creada correctamente',
        failed: 'La creación de sesión falló',
      },
      messageTest: {
        title: 'Prueba de mensaje',
        testing: 'Enviando mensaje de prueba y esperando respuesta...',
        success: 'Respuesta recibida: ',
        failed: 'No se recibió respuesta o la prueba de mensaje falló',
      },
    },
    connectionLog: 'Registro de conexión',
    testSummary: {
      allPassed: '¡Todas las pruebas pasaron! La API en tiempo real es completamente funcional.',
      someFailed: 'Algunas pruebas fallaron. Revise los errores anteriores y verifique su configuración.',
      checkList: 'Lista de verificación para solución de problemas:',
    },
    troubleshooting: [
      'Verifique que su clave API sea correcta y no haya expirado',
      'Asegúrese de que su cuenta tenga acceso a los modelos en tiempo real',
      'Revise su conexión a internet y la configuración del firewall',
      'Verifique que la URL del punto de acceso API sea correcta',
      'Si prueba desde el navegador, verifique las restricciones CORS',
      'Asegúrese de que su cuenta tenga créditos suficientes y facturación activa',
      'Contacte a su proveedor de servicio para confirmar que la API en tiempo real funcione correctamente',
    ],
    howItWorks: {
      title: 'Cómo funciona este probador',
      description: 'Este probador realiza una validación completa de los servicios de API en tiempo real a través de los siguientes pasos:',
      steps: [
        'Valida el formato de la clave API y verifica el acceso básico a la API',
        'Consulta los modelos disponibles para confirmar el acceso a modelos en tiempo real',
        'Establece una conexión WebSocket al punto de acceso en tiempo real',
        'Crea una sesión con la API en tiempo real',
        'Envía un mensaje de prueba y valida la respuesta',
      ],
    },
    supportedServices: {
      title: 'Servicios compatibles',
      description: 'Este probador funciona con los siguientes servicios:',
      list: [
        'OpenAI Realtime API (oficial)',
        'CometAPI (compatible con OpenAI)',
        'Kizuna AI API (compatible con OpenAI)',
        'Cualquier servicio en tiempo real compatible con OpenAI',
      ],
    },
    securityNote: {
      title: 'Nota de seguridad',
      content: 'Este probador utiliza el protocolo WebSocket \'openai-insecure-api-key\' para pruebas basadas en navegador, lo que expone su clave API. Esto SOLO es adecuado para pruebas y desarrollo. Para aplicaciones en producción, siempre use un servidor de retransmisión seguro para proteger sus credenciales de API.',
    },
  },
  fr: {
    pageTitle: 'Testeur d\'API temps réel',
    backLink: 'Retour aux fournisseurs d\'IA',
    overview: 'Cet outil vous permet de tester la disponibilité et la fonctionnalité de l\'API temps réel d\'OpenAI et des services compatibles OpenAI. Vous pouvez vérifier les autorisations de clé API, tester la connectivité WebSocket et envoyer des messages de test pour vous assurer que tout fonctionne correctement.',
    securityWarning: 'Avertissement de sécurité : Ce testeur s\'exécute dans votre navigateur et expose votre clé API dans la connexion WebSocket. Cette méthode est UNIQUEMENT destinée aux tests et ne doit JAMAIS être utilisée en production. Pour une utilisation en production, implémentez un serveur relais sécurisé pour gérer l\'authentification.',
    testInterface: {
      title: 'Interface de test',
      description: 'Configurez votre point d\'accès API et votre clé pour tester la disponibilité du service temps réel.',
      presetServices: 'Services préconfigurés',
      presetDescription: 'Cliquez pour remplir rapidement les points d\'accès courants :',
      apiEndpoint: 'Point d\'accès API',
      apiEndpointPlaceholder: 'ex. : https://api.openai.com',
      apiEndpointHelper: 'L\'URL de base du service API (sans /v1 ni chemins)',
      apiKey: 'Clé API',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Votre clé API pour l\'authentification',
      model: 'Modèle',
      modelHelper: 'Sélectionnez le modèle temps réel à tester',
      runTests: 'Lancer les tests',
      stopTests: 'Arrêter les tests',
      clearResults: 'Effacer les résultats',
    },
    testSteps: {
      title: 'Étapes de test',
      apiKeyValidation: {
        title: 'Validation de la clé API',
        testing: 'Validation du format et des autorisations de la clé API...',
        success: 'La clé API est valide et possède les autorisations requises',
        failed: 'La validation de la clé API a échoué',
      },
      modelAvailability: {
        title: 'Disponibilité du modèle',
        testing: 'Vérification de la disponibilité des modèles temps réel...',
        success: 'Modèles temps réel trouvés : ',
        failed: 'Aucun modèle temps réel disponible',
      },
      websocketConnection: {
        title: 'Connexion WebSocket',
        testing: 'Établissement de la connexion WebSocket à l\'API temps réel...',
        success: 'Connexion WebSocket établie avec succès',
        failed: 'La connexion WebSocket a échoué',
      },
      sessionCreation: {
        title: 'Création de session',
        testing: 'Création d\'une session temps réel...',
        success: 'Session créée avec succès',
        failed: 'La création de session a échoué',
      },
      messageTest: {
        title: 'Test de message',
        testing: 'Envoi du message de test et attente de la réponse...',
        success: 'Réponse reçue : ',
        failed: 'Aucune réponse reçue ou le test de message a échoué',
      },
    },
    connectionLog: 'Journal de connexion',
    testSummary: {
      allPassed: 'Tous les tests ont réussi ! L\'API temps réel est entièrement fonctionnelle.',
      someFailed: 'Certains tests ont échoué. Veuillez vérifier les erreurs ci-dessus et vérifier votre configuration.',
      checkList: 'Liste de vérification pour le dépannage :',
    },
    troubleshooting: [
      'Vérifiez que votre clé API est correcte et n\'a pas expiré',
      'Assurez-vous que votre compte a accès aux modèles temps réel',
      'Vérifiez votre connexion Internet et les paramètres du pare-feu',
      'Vérifiez que l\'URL du point d\'accès API est correcte',
      'Si vous testez depuis le navigateur, vérifiez les restrictions CORS',
      'Assurez-vous que votre compte dispose de crédits suffisants et d\'une facturation active',
      'Contactez votre fournisseur de service pour confirmer que l\'API temps réel fonctionne correctement',
    ],
    howItWorks: {
      title: 'Comment fonctionne ce testeur',
      description: 'Ce testeur effectue une validation complète des services d\'API temps réel à travers les étapes suivantes :',
      steps: [
        'Valide le format de la clé API et vérifie l\'accès de base à l\'API',
        'Interroge les modèles disponibles pour confirmer l\'accès aux modèles temps réel',
        'Établit une connexion WebSocket au point d\'accès temps réel',
        'Crée une session avec l\'API temps réel',
        'Envoie un message de test et valide la réponse',
      ],
    },
    supportedServices: {
      title: 'Services pris en charge',
      description: 'Ce testeur fonctionne avec les services suivants :',
      list: [
        'OpenAI Realtime API (officiel)',
        'CometAPI (compatible OpenAI)',
        'Kizuna AI API (compatible OpenAI)',
        'Tout service temps réel compatible OpenAI',
      ],
    },
    securityNote: {
      title: 'Note de sécurité',
      content: 'Ce testeur utilise le protocole WebSocket \'openai-insecure-api-key\' pour les tests basés sur le navigateur, ce qui expose votre clé API. Ceci est UNIQUEMENT adapté aux tests et au développement. Pour les applications en production, utilisez toujours un serveur relais sécurisé pour protéger vos identifiants API.',
    },
  },
  ar: {
    pageTitle: 'اختبار API الفوري',
    backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
    overview: 'تتيح لك هذه الأداة اختبار توفر ووظائف API الفوري من OpenAI والخدمات المتوافقة مع OpenAI. يمكنك التحقق من أذونات مفتاح API واختبار اتصال WebSocket وإرسال رسائل اختبار للتأكد من أن كل شيء يعمل بشكل صحيح.',
    securityWarning: 'تحذير أمني: يعمل هذا الاختبار في متصفحك ويكشف مفتاح API الخاص بك في اتصال WebSocket. هذه الطريقة مخصصة فقط لأغراض الاختبار ولا يجب استخدامها أبدًا في بيئة الإنتاج. للاستخدام في الإنتاج، قم بتطبيق خادم ترحيل آمن للتعامل مع المصادقة.',
    testInterface: {
      title: 'واجهة الاختبار',
      description: 'قم بتكوين نقطة نهاية API والمفتاح لاختبار توفر الخدمة الفورية.',
      presetServices: 'خدمات مُعدة مسبقًا',
      presetDescription: 'انقر لملء نقاط النهاية الشائعة بسرعة:',
      apiEndpoint: 'نقطة نهاية API',
      apiEndpointPlaceholder: 'مثال: https://api.openai.com',
      apiEndpointHelper: 'عنوان URL الأساسي لخدمة API (بدون /v1 أو مسارات)',
      apiKey: 'مفتاح API',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'مفتاح API الخاص بك للمصادقة',
      model: 'النموذج',
      modelHelper: 'حدد النموذج الفوري للاختبار',
      runTests: 'تشغيل الاختبارات',
      stopTests: 'إيقاف الاختبارات',
      clearResults: 'مسح النتائج',
    },
    testSteps: {
      title: 'خطوات الاختبار',
      apiKeyValidation: {
        title: 'التحقق من مفتاح API',
        testing: 'التحقق من تنسيق مفتاح API والأذونات...',
        success: 'مفتاح API صالح ولديه الأذونات المطلوبة',
        failed: 'فشل التحقق من مفتاح API',
      },
      modelAvailability: {
        title: 'توفر النموذج',
        testing: 'التحقق من توفر النماذج الفورية...',
        success: 'تم العثور على نماذج فورية: ',
        failed: 'لا توجد نماذج فورية متاحة',
      },
      websocketConnection: {
        title: 'اتصال WebSocket',
        testing: 'إنشاء اتصال WebSocket بـ API الفوري...',
        success: 'تم إنشاء اتصال WebSocket بنجاح',
        failed: 'فشل اتصال WebSocket',
      },
      sessionCreation: {
        title: 'إنشاء الجلسة',
        testing: 'إنشاء جلسة فورية...',
        success: 'تم إنشاء الجلسة بنجاح',
        failed: 'فشل إنشاء الجلسة',
      },
      messageTest: {
        title: 'اختبار الرسالة',
        testing: 'إرسال رسالة اختبار وانتظار الرد...',
        success: 'تم استلام الرد: ',
        failed: 'لم يتم استلام رد أو فشل اختبار الرسالة',
      },
    },
    connectionLog: 'سجل الاتصال',
    testSummary: {
      allPassed: 'نجحت جميع الاختبارات! API الفوري يعمل بكامل طاقته.',
      someFailed: 'فشلت بعض الاختبارات. يرجى مراجعة الأخطاء أعلاه والتحقق من التكوين.',
      checkList: 'قائمة التحقق لاستكشاف الأخطاء:',
    },
    troubleshooting: [
      'تحقق من أن مفتاح API صحيح ولم تنتهِ صلاحيته',
      'تأكد من أن حسابك لديه حق الوصول إلى النماذج الفورية',
      'تحقق من اتصالك بالإنترنت وإعدادات جدار الحماية',
      'تحقق من أن عنوان URL لنقطة نهاية API صحيح',
      'إذا كنت تختبر من المتصفح، تحقق من قيود CORS',
      'تأكد من أن حسابك لديه رصيد كافٍ وفواتير نشطة',
      'اتصل بمزود الخدمة للتأكد من أن API الفوري يعمل بشكل صحيح',
    ],
    howItWorks: {
      title: 'كيف يعمل هذا الاختبار',
      description: 'يقوم هذا الاختبار بإجراء تحقق شامل لخدمات API الفورية من خلال الخطوات التالية:',
      steps: [
        'التحقق من تنسيق مفتاح API والتحقق من الوصول الأساسي لـ API',
        'الاستعلام عن النماذج المتاحة لتأكيد الوصول إلى النماذج الفورية',
        'إنشاء اتصال WebSocket بنقطة النهاية الفورية',
        'إنشاء جلسة مع API الفوري',
        'إرسال رسالة اختبار والتحقق من الرد',
      ],
    },
    supportedServices: {
      title: 'الخدمات المدعومة',
      description: 'يعمل هذا الاختبار مع الخدمات التالية:',
      list: [
        'OpenAI Realtime API (رسمي)',
        'CometAPI (متوافق مع OpenAI)',
        'Kizuna AI API (متوافق مع OpenAI)',
        'أي خدمة فورية متوافقة مع OpenAI',
      ],
    },
    securityNote: {
      title: 'ملاحظة أمنية',
      content: 'يستخدم هذا الاختبار بروتوكول WebSocket \'openai-insecure-api-key\' للاختبار المستند إلى المتصفح، مما يكشف مفتاح API الخاص بك. هذا مناسب فقط للاختبار والتطوير. للتطبيقات في بيئة الإنتاج، استخدم دائمًا خادم ترحيل آمن لحماية بيانات اعتماد API الخاصة بك.',
    },
  },
  pt: {
    pageTitle: 'Testador de API em tempo real',
    backLink: 'Voltar para provedores de IA',
    overview: 'Esta ferramenta permite testar a disponibilidade e funcionalidade da API em tempo real da OpenAI e serviços compatíveis com OpenAI. Você pode verificar as permissões da chave API, testar a conectividade WebSocket e enviar mensagens de teste para garantir que tudo esteja funcionando corretamente.',
    securityWarning: 'Aviso de segurança: Este testador é executado no seu navegador e expõe sua chave API na conexão WebSocket. Este método é APENAS para fins de teste e NUNCA deve ser usado em produção. Para uso em produção, implemente um servidor de retransmissão seguro para lidar com a autenticação.',
    testInterface: {
      title: 'Interface de teste',
      description: 'Configure seu endpoint de API e chave para testar a disponibilidade do serviço em tempo real.',
      presetServices: 'Serviços pré-configurados',
      presetDescription: 'Clique para preencher rapidamente endpoints de serviço comuns:',
      apiEndpoint: 'Endpoint da API',
      apiEndpointPlaceholder: 'ex.: https://api.openai.com',
      apiEndpointHelper: 'A URL base do serviço de API (sem /v1 ou caminhos)',
      apiKey: 'Chave API',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Sua chave API para autenticação',
      model: 'Modelo',
      modelHelper: 'Selecione o modelo em tempo real para testar',
      runTests: 'Executar testes',
      stopTests: 'Parar testes',
      clearResults: 'Limpar resultados',
    },
    testSteps: {
      title: 'Etapas do teste',
      apiKeyValidation: {
        title: 'Validação da chave API',
        testing: 'Validando formato e permissões da chave API...',
        success: 'A chave API é válida e possui as permissões necessárias',
        failed: 'A validação da chave API falhou',
      },
      modelAvailability: {
        title: 'Disponibilidade do modelo',
        testing: 'Verificando se os modelos em tempo real estão disponíveis...',
        success: 'Modelos em tempo real encontrados: ',
        failed: 'Nenhum modelo em tempo real disponível',
      },
      websocketConnection: {
        title: 'Conexão WebSocket',
        testing: 'Estabelecendo conexão WebSocket com a API em tempo real...',
        success: 'Conexão WebSocket estabelecida com sucesso',
        failed: 'A conexão WebSocket falhou',
      },
      sessionCreation: {
        title: 'Criação de sessão',
        testing: 'Criando sessão em tempo real...',
        success: 'Sessão criada com sucesso',
        failed: 'A criação da sessão falhou',
      },
      messageTest: {
        title: 'Teste de mensagem',
        testing: 'Enviando mensagem de teste e aguardando resposta...',
        success: 'Resposta recebida: ',
        failed: 'Nenhuma resposta recebida ou o teste de mensagem falhou',
      },
    },
    connectionLog: 'Log de conexão',
    testSummary: {
      allPassed: 'Todos os testes passaram! A API em tempo real está totalmente funcional.',
      someFailed: 'Alguns testes falharam. Revise os erros acima e verifique sua configuração.',
      checkList: 'Lista de verificação para solução de problemas:',
    },
    troubleshooting: [
      'Verifique se sua chave API está correta e não expirou',
      'Certifique-se de que sua conta tenha acesso aos modelos em tempo real',
      'Verifique sua conexão com a internet e as configurações do firewall',
      'Verifique se a URL do endpoint da API está correta',
      'Se estiver testando pelo navegador, verifique as restrições CORS',
      'Certifique-se de que sua conta tenha créditos suficientes e cobrança ativa',
      'Entre em contato com seu provedor de serviço para confirmar que a API em tempo real está funcionando corretamente',
    ],
    howItWorks: {
      title: 'Como este testador funciona',
      description: 'Este testador realiza uma validação abrangente dos serviços de API em tempo real através das seguintes etapas:',
      steps: [
        'Valida o formato da chave API e verifica o acesso básico à API',
        'Consulta os modelos disponíveis para confirmar o acesso aos modelos em tempo real',
        'Estabelece uma conexão WebSocket com o endpoint em tempo real',
        'Cria uma sessão com a API em tempo real',
        'Envia uma mensagem de teste e valida a resposta',
      ],
    },
    supportedServices: {
      title: 'Serviços suportados',
      description: 'Este testador funciona com os seguintes serviços:',
      list: [
        'OpenAI Realtime API (oficial)',
        'CometAPI (compatível com OpenAI)',
        'Kizuna AI API (compatível com OpenAI)',
        'Qualquer serviço em tempo real compatível com OpenAI',
      ],
    },
    securityNote: {
      title: 'Nota de segurança',
      content: 'Este testador usa o protocolo WebSocket \'openai-insecure-api-key\' para testes baseados em navegador, o que expõe sua chave API. Isso é APENAS adequado para testes e desenvolvimento. Para aplicações em produção, sempre use um servidor de retransmissão seguro para proteger suas credenciais de API.',
    },
  },
  ru: {
    pageTitle: 'Тестер API реального времени',
    backLink: 'Назад к провайдерам ИИ',
    overview: 'Этот инструмент позволяет протестировать доступность и функциональность API реального времени OpenAI и совместимых с OpenAI сервисов. Вы можете проверить разрешения API-ключа, протестировать подключение WebSocket и отправить тестовые сообщения, чтобы убедиться, что всё работает корректно.',
    securityWarning: 'Предупреждение безопасности: Этот тестер работает в вашем браузере и раскрывает ваш API-ключ в соединении WebSocket. Этот метод предназначен ТОЛЬКО для тестирования и НИКОГДА не должен использоваться в продакшене. Для продакшена реализуйте безопасный ретранслирующий сервер для обработки аутентификации.',
    testInterface: {
      title: 'Интерфейс тестирования',
      description: 'Настройте конечную точку API и ключ для тестирования доступности сервиса реального времени.',
      presetServices: 'Предустановленные сервисы',
      presetDescription: 'Нажмите для быстрого заполнения распространённых конечных точек:',
      apiEndpoint: 'Конечная точка API',
      apiEndpointPlaceholder: 'напр., https://api.openai.com',
      apiEndpointHelper: 'Базовый URL сервиса API (без /v1 или путей)',
      apiKey: 'API-ключ',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Ваш API-ключ для аутентификации',
      model: 'Модель',
      modelHelper: 'Выберите модель реального времени для тестирования',
      runTests: 'Запустить тесты',
      stopTests: 'Остановить тесты',
      clearResults: 'Очистить результаты',
    },
    testSteps: {
      title: 'Шаги тестирования',
      apiKeyValidation: {
        title: 'Проверка API-ключа',
        testing: 'Проверка формата и разрешений API-ключа...',
        success: 'API-ключ действителен и имеет необходимые разрешения',
        failed: 'Проверка API-ключа не пройдена',
      },
      modelAvailability: {
        title: 'Доступность модели',
        testing: 'Проверка доступности моделей реального времени...',
        success: 'Найдены модели реального времени: ',
        failed: 'Нет доступных моделей реального времени',
      },
      websocketConnection: {
        title: 'Подключение WebSocket',
        testing: 'Установка соединения WebSocket с API реального времени...',
        success: 'Соединение WebSocket успешно установлено',
        failed: 'Не удалось установить соединение WebSocket',
      },
      sessionCreation: {
        title: 'Создание сессии',
        testing: 'Создание сессии реального времени...',
        success: 'Сессия успешно создана',
        failed: 'Не удалось создать сессию',
      },
      messageTest: {
        title: 'Тест сообщений',
        testing: 'Отправка тестового сообщения и ожидание ответа...',
        success: 'Получен ответ: ',
        failed: 'Ответ не получен или тест сообщений не пройден',
      },
    },
    connectionLog: 'Журнал подключения',
    testSummary: {
      allPassed: 'Все тесты пройдены! API реального времени полностью функционален.',
      someFailed: 'Некоторые тесты не пройдены. Проверьте ошибки выше и вашу конфигурацию.',
      checkList: 'Контрольный список для устранения неполадок:',
    },
    troubleshooting: [
      'Убедитесь, что ваш API-ключ правильный и срок его действия не истёк',
      'Убедитесь, что ваша учётная запись имеет доступ к моделям реального времени',
      'Проверьте подключение к интернету и настройки брандмауэра',
      'Убедитесь, что URL конечной точки API правильный',
      'При тестировании из браузера проверьте ограничения CORS',
      'Убедитесь, что на вашем счету достаточно средств и активна оплата',
      'Свяжитесь с вашим поставщиком услуг, чтобы подтвердить работоспособность API реального времени',
    ],
    howItWorks: {
      title: 'Как работает этот тестер',
      description: 'Этот тестер выполняет комплексную проверку сервисов API реального времени через следующие шаги:',
      steps: [
        'Проверяет формат API-ключа и базовый доступ к API',
        'Запрашивает доступные модели для подтверждения доступа к моделям реального времени',
        'Устанавливает соединение WebSocket с конечной точкой реального времени',
        'Создаёт сессию с API реального времени',
        'Отправляет тестовое сообщение и проверяет ответ',
      ],
    },
    supportedServices: {
      title: 'Поддерживаемые сервисы',
      description: 'Этот тестер работает со следующими сервисами:',
      list: [
        'OpenAI Realtime API (официальный)',
        'CometAPI (совместимый с OpenAI)',
        'Kizuna AI API (совместимый с OpenAI)',
        'Любой совместимый с OpenAI сервис реального времени',
      ],
    },
    securityNote: {
      title: 'Примечание о безопасности',
      content: 'Этот тестер использует протокол WebSocket \'openai-insecure-api-key\' для тестирования в браузере, что раскрывает ваш API-ключ. Это подходит ТОЛЬКО для тестирования и разработки. Для продакшн-приложений всегда используйте безопасный ретранслирующий сервер для защиты ваших учётных данных API.',
    },
  },
  de: {
    pageTitle: 'Echtzeit-API-Tester',
    backLink: 'Zurück zu KI-Anbietern',
    overview: 'Mit diesem Tool können Sie die Verfügbarkeit und Funktionalität der Echtzeit-API von OpenAI und OpenAI-kompatiblen Diensten testen. Sie können API-Schlüssel-Berechtigungen überprüfen, die WebSocket-Konnektivität testen und Testnachrichten senden, um sicherzustellen, dass alles korrekt funktioniert.',
    securityWarning: 'Sicherheitswarnung: Dieser Tester läuft in Ihrem Browser und legt Ihren API-Schlüssel in der WebSocket-Verbindung offen. Diese Methode ist NUR für Testzwecke und sollte NIEMALS in der Produktion verwendet werden. Für den Produktionseinsatz implementieren Sie einen sicheren Relay-Server für die Authentifizierung.',
    testInterface: {
      title: 'Testoberfläche',
      description: 'Konfigurieren Sie Ihren API-Endpunkt und Schlüssel, um die Verfügbarkeit des Echtzeitdienstes zu testen.',
      presetServices: 'Vorkonfigurierte Dienste',
      presetDescription: 'Klicken Sie, um häufige Dienst-Endpunkte schnell einzutragen:',
      apiEndpoint: 'API-Endpunkt',
      apiEndpointPlaceholder: 'z. B. https://api.openai.com',
      apiEndpointHelper: 'Die Basis-URL des API-Dienstes (ohne /v1 oder Pfade)',
      apiKey: 'API-Schlüssel',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Ihr API-Schlüssel zur Authentifizierung',
      model: 'Modell',
      modelHelper: 'Wählen Sie das zu testende Echtzeit-Modell',
      runTests: 'Tests ausführen',
      stopTests: 'Tests stoppen',
      clearResults: 'Ergebnisse löschen',
    },
    testSteps: {
      title: 'Testschritte',
      apiKeyValidation: {
        title: 'API-Schlüssel-Validierung',
        testing: 'Überprüfung von Format und Berechtigungen des API-Schlüssels...',
        success: 'API-Schlüssel ist gültig und hat die erforderlichen Berechtigungen',
        failed: 'API-Schlüssel-Validierung fehlgeschlagen',
      },
      modelAvailability: {
        title: 'Modellverfügbarkeit',
        testing: 'Überprüfung, ob Echtzeit-Modelle verfügbar sind...',
        success: 'Echtzeit-Modelle gefunden: ',
        failed: 'Keine Echtzeit-Modelle verfügbar',
      },
      websocketConnection: {
        title: 'WebSocket-Verbindung',
        testing: 'WebSocket-Verbindung zur Echtzeit-API wird hergestellt...',
        success: 'WebSocket-Verbindung erfolgreich hergestellt',
        failed: 'WebSocket-Verbindung fehlgeschlagen',
      },
      sessionCreation: {
        title: 'Sitzungserstellung',
        testing: 'Echtzeitsitzung wird erstellt...',
        success: 'Sitzung erfolgreich erstellt',
        failed: 'Sitzungserstellung fehlgeschlagen',
      },
      messageTest: {
        title: 'Nachrichtentest',
        testing: 'Testnachricht wird gesendet und auf Antwort gewartet...',
        success: 'Antwort erhalten: ',
        failed: 'Keine Antwort erhalten oder Nachrichtentest fehlgeschlagen',
      },
    },
    connectionLog: 'Verbindungsprotokoll',
    testSummary: {
      allPassed: 'Alle Tests bestanden! Die Echtzeit-API ist voll funktionsfähig.',
      someFailed: 'Einige Tests sind fehlgeschlagen. Bitte überprüfen Sie die obigen Fehler und Ihre Konfiguration.',
      checkList: 'Checkliste zur Fehlerbehebung:',
    },
    troubleshooting: [
      'Überprüfen Sie, ob Ihr API-Schlüssel korrekt ist und nicht abgelaufen ist',
      'Stellen Sie sicher, dass Ihr Konto Zugriff auf Echtzeit-Modelle hat',
      'Überprüfen Sie Ihre Internetverbindung und Firewall-Einstellungen',
      'Überprüfen Sie, ob die API-Endpunkt-URL korrekt ist',
      'Wenn Sie vom Browser aus testen, überprüfen Sie die CORS-Einschränkungen',
      'Stellen Sie sicher, dass Ihr Konto über ausreichend Guthaben und aktive Abrechnung verfügt',
      'Kontaktieren Sie Ihren Dienstanbieter, um zu bestätigen, dass die Echtzeit-API ordnungsgemäß funktioniert',
    ],
    howItWorks: {
      title: 'So funktioniert dieser Tester',
      description: 'Dieser Tester führt eine umfassende Validierung der Echtzeit-API-Dienste durch die folgenden Schritte durch:',
      steps: [
        'Überprüft das Format des API-Schlüssels und den grundlegenden API-Zugang',
        'Fragt verfügbare Modelle ab, um den Zugang zu Echtzeit-Modellen zu bestätigen',
        'Stellt eine WebSocket-Verbindung zum Echtzeit-Endpunkt her',
        'Erstellt eine Sitzung mit der Echtzeit-API',
        'Sendet eine Testnachricht und überprüft die Antwort',
      ],
    },
    supportedServices: {
      title: 'Unterstützte Dienste',
      description: 'Dieser Tester funktioniert mit folgenden Diensten:',
      list: [
        'OpenAI Realtime API (offiziell)',
        'CometAPI (OpenAI-kompatibel)',
        'Kizuna AI API (OpenAI-kompatibel)',
        'Jeder OpenAI-kompatible Echtzeitdienst',
      ],
    },
    securityNote: {
      title: 'Sicherheitshinweis',
      content: 'Dieser Tester verwendet das \'openai-insecure-api-key\' WebSocket-Protokoll für browserbasiertes Testen, wodurch Ihr API-Schlüssel offengelegt wird. Dies ist NUR für Tests und Entwicklung geeignet. Für Produktionsanwendungen verwenden Sie immer einen sicheren Relay-Server zum Schutz Ihrer API-Anmeldedaten.',
    },
  },
  uk: {
    pageTitle: 'Тестер API реального часу',
    backLink: 'Назад до провайдерів ШІ',
    overview: 'Цей інструмент дозволяє протестувати доступність і функціональність API реального часу OpenAI та сумісних з OpenAI сервісів. Ви можете перевірити дозволи API-ключа, протестувати підключення WebSocket та надіслати тестові повідомлення, щоб переконатися, що все працює коректно.',
    securityWarning: 'Попередження безпеки: Цей тестер працює у вашому браузері та розкриває ваш API-ключ у з\'єднанні WebSocket. Цей метод призначений ТІЛЬКИ для тестування і НІКОЛИ не повинен використовуватися у продакшені. Для продакшену реалізуйте безпечний ретрансляційний сервер для обробки автентифікації.',
    testInterface: {
      title: 'Інтерфейс тестування',
      description: 'Налаштуйте кінцеву точку API та ключ для тестування доступності сервісу реального часу.',
      presetServices: 'Попередньо налаштовані сервіси',
      presetDescription: 'Натисніть для швидкого заповнення поширених кінцевих точок:',
      apiEndpoint: 'Кінцева точка API',
      apiEndpointPlaceholder: 'напр., https://api.openai.com',
      apiEndpointHelper: 'Базова URL-адреса сервісу API (без /v1 або шляхів)',
      apiKey: 'API-ключ',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'Ваш API-ключ для автентифікації',
      model: 'Модель',
      modelHelper: 'Виберіть модель реального часу для тестування',
      runTests: 'Запустити тести',
      stopTests: 'Зупинити тести',
      clearResults: 'Очистити результати',
    },
    testSteps: {
      title: 'Кроки тестування',
      apiKeyValidation: {
        title: 'Перевірка API-ключа',
        testing: 'Перевірка формату та дозволів API-ключа...',
        success: 'API-ключ дійсний і має необхідні дозволи',
        failed: 'Перевірка API-ключа не пройдена',
      },
      modelAvailability: {
        title: 'Доступність моделі',
        testing: 'Перевірка доступності моделей реального часу...',
        success: 'Знайдено моделі реального часу: ',
        failed: 'Немає доступних моделей реального часу',
      },
      websocketConnection: {
        title: 'З\'єднання WebSocket',
        testing: 'Встановлення з\'єднання WebSocket з API реального часу...',
        success: 'З\'єднання WebSocket успішно встановлено',
        failed: 'Не вдалося встановити з\'єднання WebSocket',
      },
      sessionCreation: {
        title: 'Створення сесії',
        testing: 'Створення сесії реального часу...',
        success: 'Сесію успішно створено',
        failed: 'Не вдалося створити сесію',
      },
      messageTest: {
        title: 'Тест повідомлень',
        testing: 'Надсилання тестового повідомлення та очікування відповіді...',
        success: 'Отримано відповідь: ',
        failed: 'Відповідь не отримано або тест повідомлень не пройдено',
      },
    },
    connectionLog: 'Журнал підключення',
    testSummary: {
      allPassed: 'Усі тести пройдено! API реального часу повністю функціональний.',
      someFailed: 'Деякі тести не пройдено. Перевірте помилки вище та вашу конфігурацію.',
      checkList: 'Контрольний список для усунення неполадок:',
    },
    troubleshooting: [
      'Переконайтеся, що ваш API-ключ правильний і термін його дії не закінчився',
      'Переконайтеся, що ваш обліковий запис має доступ до моделей реального часу',
      'Перевірте підключення до інтернету та налаштування брандмауера',
      'Переконайтеся, що URL кінцевої точки API правильний',
      'При тестуванні з браузера перевірте обмеження CORS',
      'Переконайтеся, що на вашому рахунку достатньо коштів та активна оплата',
      'Зв\'яжіться з вашим постачальником послуг, щоб підтвердити працездатність API реального часу',
    ],
    howItWorks: {
      title: 'Як працює цей тестер',
      description: 'Цей тестер виконує комплексну перевірку сервісів API реального часу через наступні кроки:',
      steps: [
        'Перевіряє формат API-ключа та базовий доступ до API',
        'Запитує доступні моделі для підтвердження доступу до моделей реального часу',
        'Встановлює з\'єднання WebSocket з кінцевою точкою реального часу',
        'Створює сесію з API реального часу',
        'Надсилає тестове повідомлення та перевіряє відповідь',
      ],
    },
    supportedServices: {
      title: 'Підтримувані сервіси',
      description: 'Цей тестер працює з наступними сервісами:',
      list: [
        'OpenAI Realtime API (офіційний)',
        'CometAPI (сумісний з OpenAI)',
        'Kizuna AI API (сумісний з OpenAI)',
        'Будь-який сумісний з OpenAI сервіс реального часу',
      ],
    },
    securityNote: {
      title: 'Примітка щодо безпеки',
      content: 'Цей тестер використовує протокол WebSocket \'openai-insecure-api-key\' для тестування в браузері, що розкриває ваш API-ключ. Це підходить ТІЛЬКИ для тестування та розробки. Для продакшн-застосунків завжди використовуйте безпечний ретрансляційний сервер для захисту ваших облікових даних API.',
    },
  },
  it: {
    pageTitle: 'Tester API in tempo reale',
    backLink: 'Torna ai provider di IA',
    overview: 'Questo strumento consente di testare la disponibilità e la funzionalità dell\'API in tempo reale di OpenAI e dei servizi compatibili con OpenAI. È possibile verificare i permessi della chiave API, testare la connettività WebSocket e inviare messaggi di test per assicurarsi che tutto funzioni correttamente.',
    securityWarning: 'Avviso di sicurezza: Questo tester viene eseguito nel browser ed espone la chiave API nella connessione WebSocket. Questo metodo è SOLO per scopi di test e non deve MAI essere utilizzato in produzione. Per l\'uso in produzione, implementare un server relay sicuro per gestire l\'autenticazione.',
    testInterface: {
      title: 'Interfaccia di test',
      description: 'Configura l\'endpoint API e la chiave per testare la disponibilità del servizio in tempo reale.',
      presetServices: 'Servizi preconfigurati',
      presetDescription: 'Clicca per compilare rapidamente gli endpoint dei servizi comuni:',
      apiEndpoint: 'Endpoint API',
      apiEndpointPlaceholder: 'es., https://api.openai.com',
      apiEndpointHelper: 'L\'URL base del servizio API (senza /v1 o percorsi)',
      apiKey: 'Chiave API',
      apiKeyPlaceholder: 'sk-...',
      apiKeyHelper: 'La tua chiave API per l\'autenticazione',
      model: 'Modello',
      modelHelper: 'Seleziona il modello in tempo reale da testare',
      runTests: 'Esegui test',
      stopTests: 'Interrompi test',
      clearResults: 'Cancella risultati',
    },
    testSteps: {
      title: 'Passaggi del test',
      apiKeyValidation: {
        title: 'Validazione chiave API',
        testing: 'Validazione del formato e dei permessi della chiave API...',
        success: 'La chiave API è valida e ha i permessi richiesti',
        failed: 'Validazione della chiave API fallita',
      },
      modelAvailability: {
        title: 'Disponibilità del modello',
        testing: 'Verifica della disponibilità dei modelli in tempo reale...',
        success: 'Modelli in tempo reale trovati: ',
        failed: 'Nessun modello in tempo reale disponibile',
      },
      websocketConnection: {
        title: 'Connessione WebSocket',
        testing: 'Stabilimento della connessione WebSocket all\'API in tempo reale...',
        success: 'Connessione WebSocket stabilita con successo',
        failed: 'Connessione WebSocket fallita',
      },
      sessionCreation: {
        title: 'Creazione sessione',
        testing: 'Creazione della sessione in tempo reale...',
        success: 'Sessione creata con successo',
        failed: 'Creazione della sessione fallita',
      },
      messageTest: {
        title: 'Test del messaggio',
        testing: 'Invio del messaggio di test e attesa della risposta...',
        success: 'Risposta ricevuta: ',
        failed: 'Nessuna risposta ricevuta o test del messaggio fallito',
      },
    },
    connectionLog: 'Log di connessione',
    testSummary: {
      allPassed: 'Tutti i test superati! L\'API in tempo reale è completamente funzionale.',
      someFailed: 'Alcuni test sono falliti. Verifica gli errori sopra e controlla la configurazione.',
      checkList: 'Lista di controllo per la risoluzione dei problemi:',
    },
    troubleshooting: [
      'Verifica che la chiave API sia corretta e non sia scaduta',
      'Assicurati che il tuo account abbia accesso ai modelli in tempo reale',
      'Controlla la connessione Internet e le impostazioni del firewall',
      'Verifica che l\'URL dell\'endpoint API sia corretto',
      'Se stai testando dal browser, controlla le restrizioni CORS',
      'Assicurati che il tuo account abbia crediti sufficienti e fatturazione attiva',
      'Contatta il tuo fornitore di servizi per confermare che l\'API in tempo reale funzioni correttamente',
    ],
    howItWorks: {
      title: 'Come funziona questo tester',
      description: 'Questo tester esegue una validazione completa dei servizi API in tempo reale attraverso i seguenti passaggi:',
      steps: [
        'Valida il formato della chiave API e verifica l\'accesso di base all\'API',
        'Interroga i modelli disponibili per confermare l\'accesso ai modelli in tempo reale',
        'Stabilisce una connessione WebSocket all\'endpoint in tempo reale',
        'Crea una sessione con l\'API in tempo reale',
        'Invia un messaggio di test e valida la risposta',
      ],
    },
    supportedServices: {
      title: 'Servizi supportati',
      description: 'Questo tester funziona con i seguenti servizi:',
      list: [
        'OpenAI Realtime API (ufficiale)',
        'CometAPI (compatibile con OpenAI)',
        'Kizuna AI API (compatibile con OpenAI)',
        'Qualsiasi servizio in tempo reale compatibile con OpenAI',
      ],
    },
    securityNote: {
      title: 'Nota sulla sicurezza',
      content: 'Questo tester utilizza il protocollo WebSocket \'openai-insecure-api-key\' per i test basati su browser, il che espone la chiave API. Questo è adatto SOLO per test e sviluppo. Per le applicazioni in produzione, utilizza sempre un server relay sicuro per proteggere le credenziali API.',
    },
  },
};

type StepStatus = 'pending' | 'testing' | 'success' | 'failed';

interface TestStep {
  id: string;
  status: StepStatus;
  details: string;
}

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

const MODELS = [
  // GA models
  'gpt-realtime',
  'gpt-realtime-2025-08-28',
  'gpt-realtime-mini',
  'gpt-realtime-mini-2025-12-15',
  'gpt-realtime-mini-2025-10-06',
  // Preview models
  'gpt-4o-realtime-preview',
  'gpt-4o-realtime-preview-2025-06-03',
  'gpt-4o-realtime-preview-2024-12-17',
  'gpt-4o-mini-realtime-preview',
  'gpt-4o-mini-realtime-preview-2024-12-17',
];

const PRESETS = {
  openai: 'https://api.openai.com',
  cometapi: 'https://api.cometapi.com',
  kizunaai: 'https://api.kizuna.ai',
};

export function RealtimeAPITester() {
  const { locale } = useI18n();
  const t = translations[locale] || translations.en;
  useDocumentMeta(t.pageTitle, t.overview);

  const [apiEndpoint, setApiEndpoint] = useState('https://api.openai.com');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gpt-realtime');
  const [isRunning, setIsRunning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [steps, setSteps] = useState<TestStep[]>([
    { id: 'api-key', status: 'pending', details: '' },
    { id: 'model', status: 'pending', details: '' },
    { id: 'websocket', status: 'pending', details: '' },
    { id: 'session', status: 'pending', details: '' },
    { id: 'message', status: 'pending', details: '' },
  ]);
  const [testSummary, setTestSummary] = useState<{ passed: number; total: number } | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const abortedRef = useRef(false);

  const addLog = useCallback((message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  }, []);

  const updateStep = useCallback((id: string, status: StepStatus, details = '') => {
    if (abortedRef.current) return;
    setSteps(prev => prev.map(step =>
      step.id === id ? { ...step, status, details } : step
    ));
  }, []);

  const setPreset = (preset: keyof typeof PRESETS) => {
    setApiEndpoint(PRESETS[preset]);
  };

  const runTests = async () => {
    if (!apiEndpoint || !apiKey) {
      alert('Please enter both API endpoint and API key');
      return;
    }

    // Reset state
    abortedRef.current = false;
    setIsRunning(true);
    setShowResults(true);
    setLogs([]);
    setTestSummary(null);
    setSteps([
      { id: 'api-key', status: 'pending', details: '' },
      { id: 'model', status: 'pending', details: '' },
      { id: 'websocket', status: 'pending', details: '' },
      { id: 'session', status: 'pending', details: '' },
      { id: 'message', status: 'pending', details: '' },
    ]);

    const cleanEndpoint = apiEndpoint.replace(/\/+$/, '');
    let passedTests = 0;
    const totalTests = 5;

    try {
      // Step 1: API Key Validation
      updateStep('api-key', 'testing');
      addLog('Starting API key validation...', 'info');

      const modelsResponse = await fetch(`${cleanEndpoint}/v1/models`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });

      if (!modelsResponse.ok) {
        throw new Error(`API key validation failed: ${modelsResponse.status} ${modelsResponse.statusText}`);
      }

      interface ModelsResponse {
        data: Array<{ id: string }>;
      }

      const modelsData: ModelsResponse = await modelsResponse.json();
      updateStep('api-key', 'success', t.testSteps.apiKeyValidation.success);
      addLog('API key validated successfully', 'success');
      passedTests++;

      if (abortedRef.current) return;

      // Step 2: Model Availability
      updateStep('model', 'testing');
      addLog('Checking for realtime models...', 'info');

      const realtimeModels = modelsData.data.filter(m =>
        m.id.includes('realtime') || m.id === model
      );

      if (realtimeModels.length > 0) {
        const modelNames = realtimeModels.map(m => m.id).join(', ');
        updateStep('model', 'success', t.testSteps.modelAvailability.success + modelNames);
        addLog(`Found ${realtimeModels.length} realtime model(s): ${modelNames}`, 'success');
        passedTests++;
      } else {
        updateStep('model', 'failed', t.testSteps.modelAvailability.failed);
        addLog('No realtime models available', 'error');
      }

      if (abortedRef.current) return;

      // Step 3: WebSocket Connection
      updateStep('websocket', 'testing');
      addLog('Establishing WebSocket connection...', 'info');

      const wsUrl = cleanEndpoint.replace(/^https?:/, 'wss:') + '/v1/realtime?model=' + model;

      wsRef.current = new WebSocket(wsUrl, [
        'realtime',
        `openai-insecure-api-key.${apiKey}`,
        'openai-beta.realtime-v1'
      ]);

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        wsRef.current!.onopen = () => {
          clearTimeout(timeout);
          updateStep('websocket', 'success', t.testSteps.websocketConnection.success);
          addLog('WebSocket connection established', 'success');
          passedTests++;
          resolve();
        };

        wsRef.current!.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('WebSocket connection failed'));
        };
      });

      if (abortedRef.current) return;

      // Step 4: Session Creation
      updateStep('session', 'testing');
      addLog('Creating realtime session...', 'info');

      let sessionCreated = false;
      let messageResponseReceived = false;

      wsRef.current!.onmessage = (event) => {
        const data = JSON.parse(event.data);
        addLog(`Received: ${data.type}`, 'info');

        if (data.type === 'error') {
          const errorMsg = data.error?.message || 'Unknown error';
          addLog(`Error: ${errorMsg}`, 'error');
          if (!sessionCreated) {
            updateStep('session', 'failed', `Error: ${errorMsg}`);
          }
        } else if (data.type === 'session.created') {
          sessionCreated = true;
          updateStep('session', 'success', t.testSteps.sessionCreation.success);
          addLog('Session created with ID: ' + data.session.id, 'success');
        } else if (data.type === 'response.done' || data.type === 'response.text.done') {
          messageResponseReceived = true;
          let responseText = 'Response received';

          if (data.response?.output && Array.isArray(data.response.output)) {
            for (const output of data.response.output) {
              if (output.content && Array.isArray(output.content)) {
                const textContent = output.content.find((c: { text?: string; transcript?: string }) => c.text || c.transcript);
                if (textContent) {
                  responseText = textContent.text || textContent.transcript || responseText;
                  break;
                }
              } else if (output.text) {
                responseText = output.text;
                break;
              } else if (output.transcript) {
                responseText = output.transcript;
                break;
              }
            }
          }

          updateStep('message', 'success', t.testSteps.messageTest.success + responseText);
          addLog('Received response: ' + responseText, 'success');
        }
      };

      // Wait for session creation
      await new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          if (sessionCreated || abortedRef.current) {
            clearInterval(checkInterval);
            if (sessionCreated) passedTests++;
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!sessionCreated && !abortedRef.current) {
            updateStep('session', 'failed', t.testSteps.sessionCreation.failed);
            addLog('Session creation timeout', 'error');
          }
          resolve();
        }, 5000);
      });

      if (abortedRef.current || !sessionCreated) {
        setTestSummary({ passed: passedTests, total: totalTests });
        return;
      }

      // Step 5: Message Test
      updateStep('message', 'testing');
      addLog('Sending test message...', 'info');

      const testMessage = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: 'Hello! Please respond with a simple greeting.' }]
        }
      };

      wsRef.current!.send(JSON.stringify(testMessage));
      addLog('Sent test message', 'info');

      wsRef.current!.send(JSON.stringify({ type: 'response.create' }));

      // Wait for response
      await new Promise<void>((resolve) => {
        const checkInterval = setInterval(() => {
          if (messageResponseReceived || abortedRef.current) {
            clearInterval(checkInterval);
            if (messageResponseReceived) passedTests++;
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!messageResponseReceived && !abortedRef.current) {
            updateStep('message', 'failed', t.testSteps.messageTest.failed);
            addLog('No response received within timeout', 'error');
          }
          resolve();
        }, 10000);
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Error: ${errorMessage}`, 'error');

      // Update the step that failed
      const testingStep = steps.find(s => s.status === 'testing');
      if (testingStep) {
        updateStep(testingStep.id, 'failed', errorMessage);
      }
    } finally {
      // Clean up
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
        addLog('WebSocket connection closed', 'info');
      }

      if (!abortedRef.current) {
        setTestSummary({ passed: passedTests, total: totalTests });
      }

      setIsRunning(false);
    }
  };

  const stopTests = () => {
    abortedRef.current = true;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    setIsRunning(false);
    addLog('Tests aborted by user', 'error');
  };

  const clearResults = () => {
    setShowResults(false);
    setLogs([]);
    setTestSummary(null);
    setSteps([
      { id: 'api-key', status: 'pending', details: '' },
      { id: 'model', status: 'pending', details: '' },
      { id: 'websocket', status: 'pending', details: '' },
      { id: 'session', status: 'pending', details: '' },
      { id: 'message', status: 'pending', details: '' },
    ]);
  };

  const getStepTitle = (id: string): string => {
    switch (id) {
      case 'api-key': return t.testSteps.apiKeyValidation.title;
      case 'model': return t.testSteps.modelAvailability.title;
      case 'websocket': return t.testSteps.websocketConnection.title;
      case 'session': return t.testSteps.sessionCreation.title;
      case 'message': return t.testSteps.messageTest.title;
      default: return '';
    }
  };

  return (
    <div className="docs-content tutorial-page">
      <Link to={localizePath('/docs/ai-providers', locale)} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <section className="tutorial-page__section">
        <p>{t.overview}</p>
        <div className="tutorial-page__tip" style={{ borderLeftColor: '#e74c3c', backgroundColor: '#fff3cd' }}>
          <p><strong>{t.securityWarning}</strong></p>
        </div>
      </section>

      <section className="tutorial-page__section">
        <h2>{t.testInterface.title}</h2>
        <div className="api-tester">
          <p>{t.testInterface.description}</p>

          <div className="api-tester__field">
            <label>{t.testInterface.presetServices}</label>
            <p className="api-tester__helper">{t.testInterface.presetDescription}</p>
            <div className="api-tester__presets">
              <button type="button" onClick={() => setPreset('openai')}>OpenAI</button>
              <button type="button" onClick={() => setPreset('cometapi')}>CometAPI</button>
              <button type="button" onClick={() => setPreset('kizunaai')}>Kizuna AI</button>
            </div>
          </div>

          <div className="api-tester__field">
            <label htmlFor="apiEndpoint">{t.testInterface.apiEndpoint}</label>
            <input
              type="text"
              id="apiEndpoint"
              value={apiEndpoint}
              onChange={(e) => setApiEndpoint(e.target.value)}
              placeholder={t.testInterface.apiEndpointPlaceholder}
            />
            <p className="api-tester__helper">{t.testInterface.apiEndpointHelper}</p>
          </div>

          <div className="api-tester__field">
            <label htmlFor="apiKey">{t.testInterface.apiKey}</label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t.testInterface.apiKeyPlaceholder}
              onKeyPress={(e) => e.key === 'Enter' && !isRunning && runTests()}
            />
            <p className="api-tester__helper">{t.testInterface.apiKeyHelper}</p>
          </div>

          <div className="api-tester__field">
            <label htmlFor="model">{t.testInterface.model}</label>
            <select
              id="model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {MODELS.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <p className="api-tester__helper">{t.testInterface.modelHelper}</p>
          </div>

          <div className="api-tester__actions">
            {!isRunning ? (
              <button type="button" className="api-tester__btn api-tester__btn--primary" onClick={runTests}>
                <Play size={16} />
                {t.testInterface.runTests}
              </button>
            ) : (
              <button type="button" className="api-tester__btn api-tester__btn--danger" onClick={stopTests}>
                <Square size={16} />
                {t.testInterface.stopTests}
              </button>
            )}
            {showResults && (
              <button type="button" className="api-tester__btn" onClick={clearResults}>
                <Trash2 size={16} />
                {t.testInterface.clearResults}
              </button>
            )}
          </div>
        </div>
      </section>

      {showResults && (
        <section className="tutorial-page__section">
          <h2>{t.testSteps.title}</h2>
          <div className="api-tester__results">
            {steps.map((step, index) => (
              <div key={step.id} className={`api-tester__step api-tester__step--${step.status}`}>
                <div className="api-tester__step-title">
                  {index + 1}. {getStepTitle(step.id)}
                </div>
                {step.details && (
                  <div className="api-tester__step-details">{step.details}</div>
                )}
              </div>
            ))}

            <h3>{t.connectionLog}</h3>
            <div className="api-tester__log">
              {logs.map((log, index) => (
                <div key={index} className={`api-tester__log-entry api-tester__log-entry--${log.type}`}>
                  [{log.timestamp}] {log.message}
                </div>
              ))}
            </div>

            {testSummary && (
              <div className={`api-tester__summary api-tester__summary--${testSummary.passed === testSummary.total ? 'success' : 'failed'}`}>
                {testSummary.passed === testSummary.total ? (
                  <p>{t.testSummary.allPassed}</p>
                ) : (
                  <>
                    <p>{t.testSummary.someFailed}</p>
                    <p><strong>{t.testSummary.checkList}</strong></p>
                    <ul>
                      {t.troubleshooting.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="tutorial-page__section">
        <h2>{t.howItWorks.title}</h2>
        <div className="tutorial-page__step">
          <p>{t.howItWorks.description}</p>
          <ol>
            {t.howItWorks.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </div>
      </section>

      <section className="tutorial-page__section">
        <h2>{t.supportedServices.title}</h2>
        <div className="tutorial-page__step">
          <p>{t.supportedServices.description}</p>
          <ul>
            {t.supportedServices.list.map((service, index) => (
              <li key={index}>{service}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="tutorial-page__section">
        <div className="tutorial-page__tip">
          <h4>{t.securityNote.title}</h4>
          <p>{t.securityNote.content}</p>
        </div>
      </section>
    </div>
  );
}
